import React, { useState, useEffect } from 'react';
import { Query, useMutation, useQuery } from 'react-apollo';
import { useSelector } from 'react-redux';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { GET_STUDENT_MILESTONES } from 'graphql/student/milestones';
import Helmet from 'react-helmet';
import withConfirm from 'hooks/withConfirm';
import Carousel from './components/Carousel';
import { makeStyles } from '@material-ui/core';
import ReferralCard from 'modules/referral/components/ReferralCard';
import { GET_REFERRAL } from 'graphql/referral';
import { INSERT_CONTACTS } from 'graphql/referral/contacts';
import useSnack from 'hooks/useSnack';

const useStyles = makeStyles((theme) => ({
  upcomingMilestones: {
    'cursor': 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
}));

const Milestones = ({ location }) => {
  const classes = useStyles();
  const user = useSelector((s) => s.user.user);
  const profile = useSelector((s) => s.student.profile);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [listLength, setListLength] = useState(4);

  const { data: referralData } = useQuery(GET_REFERRAL, {
    variables: { userId: user.id },
  });

  const handleClick = (idx) => {
    setCurrentIndex(idx);
  };

  useEffect(() => {
    if (location?.state) {
      setCurrentIndex(location?.state.currentIndex);
    }
  }, [location]);

  const handleSeeMore = (total) => {
    let len = listLength + 4;
    if (len > total) setListLength(total);
    else setListLength(len);
  };

  return (
    <div>
      <Helmet title="Timeline" />
      <Query
        query={GET_STUDENT_MILESTONES}
        fetchPolicy="cache-and-network"
        variables={{ userId: user.id }}
      >
        {({ data, loading }) => {
          if (!data) {
            return <CircularProgress />;
          }

          const milestones = data?.milestones;
          const totalMilestones = milestones?.length || 0;
          const doneMilestones =
            totalMilestones > 0
              ? milestones.filter((f) => f.status === 'done').length
              : 0;
          const upcomingMilestones =
            totalMilestones > 0
              ? milestones.filter(
                  (f) => f.status === null && f.name !== 'FA Session',
                ).length
              : 0;

          return (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={8}>
                <Typography paragraph variant="h5">
                  Your progress
                </Typography>
                <Carousel
                  currentIndex={currentIndex}
                  setCurrentIndex={setCurrentIndex}
                  totalMilestones={totalMilestones}
                  doneMilestones={doneMilestones}
                  milestones={data.milestones}
                  user={user}
                  student={{
                    ...user,
                    ...profile,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="h5">Upcoming Milestones</Typography>
                <List>
                  {data.milestones
                    .filter((f) => f.status === null && f.name !== 'FA Session')
                    .map(
                      (i, key) =>
                        key < listLength && (
                          <ListItem
                            className={classes.upcomingMilestones}
                            onClick={() => handleClick(i.order)}
                            divider
                            key={i.id}
                            disableGutters
                          >
                            <ListItemText
                              primary={i.name}
                              primaryTypographyProps={{ variant: 'body2' }}
                              secondary={`${i.exercises.length} Exercises`}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItem>
                        ),
                    )}
                </List>
                {listLength < upcomingMilestones && (
                  <Button
                    fullWidth
                    size="large"
                    color="primary"
                    variant="outlined"
                    onClick={() => handleSeeMore(upcomingMilestones)}
                  >
                    See more
                  </Button>
                )}
                {referralData?.referrals.length !== 0 && (
                  <Box mt={3}>
                    <ReferralCard referral={referralData.referrals[0]} />
                  </Box>
                )}
              </Grid>
            </Grid>
          );
        }}
      </Query>
    </div>
  );
};

export default withConfirm(Milestones);
